// src/services/accessPolicyService.ts

import { API_ENDPOINT, REQUEST_TIMEOUT_MS } from '../models/constants';
import type { AccessPolicy } from '../models/types';

export interface IAccessPolicyService {
  fetchPolicy(idToken: string, userId?: string): Promise<AccessPolicy>;
}

export class AccessPolicyService implements IAccessPolicyService {
  async fetchPolicy(idToken: string, userId?: string): Promise<AccessPolicy> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    const url = userId
      ? `${API_ENDPOINT}/access-policy/${userId}`
      : `${API_ENDPOINT}/access-policy`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
        signal: controller.signal,
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: invalid or expired token');
        }
        throw new Error(`Failed to fetch access policy: ${response.status} ${response.statusText}`);
      }

      const data: AccessPolicy = await response.json();
      return data;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

// Pure functions for policy evaluation

/** Wildcard value — if present in allowedYears or fullYearAccess, grants access to everything */
const WILDCARD = '*';

export function isYearAllowed(year: string, policy: AccessPolicy): boolean {
  if (policy.fullYearAccess.includes(WILDCARD) || policy.allowedYears.includes(WILDCARD)) {
    return true;
  }
  return (
    policy.fullYearAccess.includes(year) ||
    policy.allowedYears.includes(year) ||
    year in policy.allowedEvents
  );
}

export function isEventAllowed(year: string, event: string, policy: AccessPolicy): boolean {
  if (policy.fullYearAccess.includes(WILDCARD)) return true;
  if (policy.fullYearAccess.includes(year)) return true;
  const events = policy.allowedEvents[year];
  return events != null && events.includes(event);
}

export function getVisibleYears(allYears: string[], policy: AccessPolicy): string[] {
  if (policy.fullYearAccess.includes(WILDCARD) || policy.allowedYears.includes(WILDCARD)) {
    return allYears;
  }
  return allYears.filter((y) => isYearAllowed(y, policy));
}

export function getVisibleEvents(year: string, allEvents: string[], policy: AccessPolicy): string[] {
  if (policy.fullYearAccess.includes(WILDCARD)) return allEvents;
  return allEvents.filter((e) => isEventAllowed(year, e, policy));
}
